import React, { useEffect, useState } from 'react';
import {
  Mail, Send, Inbox, CheckCircle2, XCircle, Save, RefreshCw, Shield, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { emailAPI } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import './EmailSettings.css';

const EmailSettings = () => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtpTest, setSmtpTest] = useState(null);
  const [imapTest, setImapTest] = useState(null);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [checkingInbox, setCheckingInbox] = useState(false);
  const [inboxResult, setInboxResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [cfgRes, logRes, statsRes] = await Promise.all([
        emailAPI.getConfig(),
        emailAPI.getLog({ limit: 20 }),
        emailAPI.getStats(),
      ]);
      setConfig(cfgRes.data);
      setForm(cfgRes.data);
      setLogs(logRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('E-Mail-Konfiguration konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data } = await emailAPI.updateConfig(form);
      setConfig(data);
      setForm(data);
      toast.success('E-Mail-Konfiguration gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    try {
      setTestingSmtp(true);
      setSmtpTest(null);
      const { data } = await emailAPI.testSmtp();
      setSmtpTest(data);
      if (data.ok) toast.success('SMTP OK'); else toast.error(data.message);
    } catch (err) {
      setSmtpTest({ ok: false, message: err.response?.data?.detail || 'Test fehlgeschlagen' });
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTestImap = async () => {
    try {
      setTestingImap(true);
      setImapTest(null);
      const { data } = await emailAPI.testImap();
      setImapTest(data);
      if (data.ok) toast.success('IMAP OK'); else toast.error(data.message);
    } catch (err) {
      setImapTest({ ok: false, message: err.response?.data?.detail || 'Test fehlgeschlagen' });
    } finally {
      setTestingImap(false);
    }
  };

  const handleCheckInbox = async () => {
    try {
      setCheckingInbox(true);
      const { data } = await emailAPI.checkInbox();
      setInboxResult(data);
      if (data.invoices_created > 0) {
        toast.success(`${data.invoices_created} Rechnung(en) importiert`);
      } else {
        toast.success('Keine neuen Rechnungen im Postfach');
      }
      load(); // refresh logs
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Postfach-Prüfung fehlgeschlagen');
    } finally {
      setCheckingInbox(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner size="large" /></div>;
  }

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>E-Mail-Konfiguration</h1>
          <p>SMTP-Versand und IMAP-Empfang für Rechnungen konfigurieren</p>
        </div>
      </div>

      <div className="content-body">
        {/* Stats overview */}
        {stats && (
          <div className="email-stats-row mb-6">
            <div className="email-stat-card">
              <Send size={20} />
              <div>
                <span className="stat-number">{stats.sent}</span>
                <span className="stat-label">Versendet</span>
              </div>
            </div>
            <div className="email-stat-card">
              <Inbox size={20} />
              <div>
                <span className="stat-number">{stats.received}</span>
                <span className="stat-label">Empfangen</span>
              </div>
            </div>
            <div className="email-stat-card warn">
              <AlertTriangle size={20} />
              <div>
                <span className="stat-number">{stats.failed}</span>
                <span className="stat-label">Fehlgeschlagen</span>
              </div>
            </div>
          </div>
        )}

        {/* SMTP Config */}
        <div className="card mb-6">
          <div className="card-header company-header-row">
            <h2><Send size={18} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '0.4rem' }} />SMTP — Versand</h2>
            <div className="flex gap-2">
              <button className="btn btn-outline btn-sm" onClick={handleTestSmtp} disabled={testingSmtp}>
                <Shield size={14} /> {testingSmtp ? 'Teste...' : 'Verbindung testen'}
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="email-config-grid">
              <div className="form-group">
                <label className="form-label">SMTP Server</label>
                <input className="form-input" value={form.smtp_host || ''} onChange={(e) => handleChange('smtp_host', e.target.value)} placeholder="smtp.ionos.de" />
              </div>
              <div className="form-group">
                <label className="form-label">Port</label>
                <input className="form-input" type="number" value={form.smtp_port || ''} onChange={(e) => handleChange('smtp_port', parseInt(e.target.value) || 465)} />
              </div>
              <div className="form-group">
                <label className="form-label">SSL/TLS</label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={!!form.smtp_use_ssl} onChange={(e) => handleChange('smtp_use_ssl', e.target.checked)} />
                  <span>{form.smtp_use_ssl ? 'SSL aktiv' : 'Deaktiviert'}</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Benutzername</label>
                <input className="form-input" value={form.smtp_user || ''} onChange={(e) => handleChange('smtp_user', e.target.value)} placeholder="info@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Passwort</label>
                <input className="form-input" type="password" value={form.smtp_password || ''} onChange={(e) => handleChange('smtp_password', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Absender-Adresse</label>
                <input className="form-input" value={form.smtp_from_email || ''} onChange={(e) => handleChange('smtp_from_email', e.target.value)} placeholder="rechnung@example.com" />
              </div>
            </div>
            {smtpTest && (
              <div className={`test-result mt-3 ${smtpTest.ok ? 'ok' : 'fail'}`}>
                {smtpTest.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                <span>{smtpTest.message}</span>
              </div>
            )}
          </div>
        </div>

        {/* IMAP Config */}
        <div className="card mb-6">
          <div className="card-header company-header-row">
            <h2><Inbox size={18} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '0.4rem' }} />IMAP — Empfang</h2>
            <div className="flex gap-2">
              <button className="btn btn-outline btn-sm" onClick={handleTestImap} disabled={testingImap}>
                <Shield size={14} /> {testingImap ? 'Teste...' : 'Verbindung testen'}
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleCheckInbox} disabled={checkingInbox}>
                <RefreshCw size={14} className={checkingInbox ? 'spin' : ''} />
                {checkingInbox ? 'Prüfe...' : 'Postfach prüfen'}
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="email-config-grid">
              <div className="form-group">
                <label className="form-label">IMAP Server</label>
                <input className="form-input" value={form.imap_host || ''} onChange={(e) => handleChange('imap_host', e.target.value)} placeholder="imap.ionos.de" />
              </div>
              <div className="form-group">
                <label className="form-label">Port</label>
                <input className="form-input" type="number" value={form.imap_port || ''} onChange={(e) => handleChange('imap_port', parseInt(e.target.value) || 993)} />
              </div>
              <div className="form-group">
                <label className="form-label">SSL/TLS</label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={!!form.imap_use_ssl} onChange={(e) => handleChange('imap_use_ssl', e.target.checked)} />
                  <span>{form.imap_use_ssl ? 'SSL aktiv' : 'Deaktiviert'}</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Benutzername</label>
                <input className="form-input" value={form.imap_user || ''} onChange={(e) => handleChange('imap_user', e.target.value)} placeholder="rechnung@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Passwort</label>
                <input className="form-input" type="password" value={form.imap_password || ''} onChange={(e) => handleChange('imap_password', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Ordner</label>
                <input className="form-input" value={form.imap_folder || ''} onChange={(e) => handleChange('imap_folder', e.target.value)} placeholder="INBOX" />
              </div>
            </div>
            {imapTest && (
              <div className={`test-result mt-3 ${imapTest.ok ? 'ok' : 'fail'}`}>
                {imapTest.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                <span>{imapTest.message}</span>
              </div>
            )}
            {inboxResult && (
              <div className="test-result mt-3 ok">
                <CheckCircle2 size={16} />
                <span>{inboxResult.invoices_created} neue Rechnung(en) importiert</span>
              </div>
            )}
          </div>
        </div>

        {/* Save button */}
        <div className="mb-6">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={16} /> {saving ? 'Speichert...' : 'Konfiguration speichern'}
          </button>
        </div>

        {/* Email Log */}
        <div className="card">
          <div className="card-header company-header-row">
            <h2><Mail size={18} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '0.4rem' }} />E-Mail-Protokoll</h2>
            <button className="btn btn-outline btn-sm" onClick={load}>
              <RefreshCw size={14} /> Aktualisieren
            </button>
          </div>
          <div className="card-body">
            {logs.length === 0 ? (
              <p className="text-muted">Noch keine E-Mails gesendet oder empfangen.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Richtung</th>
                    <th>Typ</th>
                    <th>An / Von</th>
                    <th>Betreff</th>
                    <th>Status</th>
                    <th>Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        <span className={`direction-badge dir-${entry.direction}`}>
                          {entry.direction === 'outgoing' ? 'Ausgang' : 'Eingang'}
                        </span>
                      </td>
                      <td className="text-sm">{entry.email_type || '—'}</td>
                      <td className="text-sm">{entry.direction === 'outgoing' ? entry.to_email : entry.from_email}</td>
                      <td className="text-sm">{entry.subject ? (entry.subject.length > 50 ? entry.subject.slice(0, 50) + '...' : entry.subject) : '—'}</td>
                      <td>
                        <span className={`log-status log-${entry.status}`}>
                          {entry.status === 'sent' && 'Gesendet'}
                          {entry.status === 'failed' && 'Fehler'}
                          {entry.status === 'received' && 'Empfangen'}
                          {entry.status === 'processed' && 'Verarbeitet'}
                        </span>
                      </td>
                      <td className="text-sm text-muted">
                        {new Date(entry.created_at).toLocaleString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
